/**
 * AppShell — composes the persistent chrome: TopNav, main outlet, FAB,
 * Capture sheet, Toast viewport.
 *
 * Also wires global shortcuts:
 *   Cmd/Ctrl+K        → open Capture sheet (UX-AC-018 / AC-076)
 *   Esc               → close Capture sheet (when open)
 *   Cmd/Ctrl+Shift+H  → open household switcher (UX-AC-011 / AC-017)
 *   /                 → focus search (no-op v1, would land in /places)
 */
import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { TopNav } from './ui/TopNav';
import { BottomNav } from './ui/BottomNav';
import { FAB } from './ui/FAB';
import { CaptureSheet } from './CaptureSheet';
import { KeyboardCheatSheet } from './KeyboardCheatSheet';
import { ToastViewport } from './ui/Toast';
import { useAppStore } from '../lib/store';
import { useOnline } from '../lib/online';

function isTypingInField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function AppShell() {
  const navigate = useNavigate();
  const online = useOnline();
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac =
        typeof navigator !== 'undefined' &&
        /Mac|iPhone|iPad/.test(navigator.platform);
      const accel = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + K — open capture
      if (accel && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        useAppStore.getState().openCaptureSheet();
        return;
      }
      // Cmd/Ctrl + Shift + H — household switcher
      if (accel && e.shiftKey && (e.key === 'h' || e.key === 'H')) {
        e.preventDefault();
        useAppStore.getState().openHouseholdSwitcher();
        return;
      }
      // Esc — close any sheet
      if (e.key === 'Escape') {
        if (useAppStore.getState().capture.sheetOpen) {
          useAppStore.getState().closeCaptureSheet();
        }
        // Also close the shortcuts modal if it's open.
        setShortcutsOpen((prev) => (prev ? false : prev));
        return;
      }
      // "?" — show the keyboard shortcuts cheat-sheet. We require the
      // literal `?` key (Shift + /) and avoid triggering when the user is
      // typing in any input.
      if (e.key === '?' && !accel && !isTypingInField(e.target)) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      // "/" focuses search (on /places when implemented). v1 placeholder:
      // navigate to /places + autofocus.
      if (e.key === '/' && !accel && !isTypingInField(e.target)) {
        e.preventDefault();
        navigate('/places');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-bg text-ink-900 flex flex-col">
      {!online ? (
        <div
          role="status"
          aria-live="polite"
          className="bg-surface-2 border-b border-ink-200 px-5 py-2 text-center font-sans text-[0.65rem] uppercase tracking-[0.18em] text-ink-700"
          data-testid="offline-banner"
        >
          Offline — recent entries still readable.
        </div>
      ) : null}
      <TopNav />
      <main className="flex-1 mx-auto w-full max-w-content px-5 py-8 md:px-8 md:py-12">
        <Outlet />
      </main>
      <BottomNav />
      <FAB />
      <CaptureSheet />
      <KeyboardCheatSheet
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />
      <ToastViewport />
    </div>
  );
}
